import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { useSearch } from '@tanstack/react-router'
import { HandCoins } from 'lucide-react'
import { useState, type KeyboardEvent, type ReactNode } from 'react'

import {
  COLLECTION_FILTERS,
  type CollectionFilter,
  type CollectionQueueItem,
} from '@aeris/contracts'
import { parseAmount } from '@aeris/finance'
import { formatMoney } from '@aeris/money'
import { COLLECTION_FILTER_LABELS } from '@aeris/status-meta'
import {
  Avatar,
  Badge,
  EmptyState,
  LoadFailed,
  Page,
  PageHeader,
  Pagination,
  SearchInput,
  Select,
  Spinner,
  StatTile,
  Tabs,
  TabsList,
  TabsTrigger,
  cn,
} from '@aeris/ui'

import { CollectionPanel } from '../components/collection-panel.tsx'
import { fetchCollectionQueue } from '../lib/api/collections.ts'
import { useDebounced } from '../lib/use-debounced.ts'

/**
 * Cobranças (F135).
 *
 * *"uma página pra cobranças com UI UX foda"*. A tela é uma fila de trabalho,
 * não um relatório: à esquerda quem ligar agora, do atraso mais antigo para o
 * mais novo; à direita o cliente escolhido com tudo para resolver ali —
 * WhatsApp, registrar a promessa, receber, renegociar e o extrato.
 *
 * O cliente que prometeu pagar para uma data que ainda não chegou sai de
 * "Atrasados" e volta sozinho no dia ("Promessa hoje") ou depois ("Promessa
 * quebrada"): a fila mostra o que precisa de alguém hoje.
 *
 * `↑`/`↓` (ou `j`/`k`) andam na fila, para quem cobra vinte clientes seguidos.
 */

const PAGE_SIZE = 25

const TABS = COLLECTION_FILTERS.map((value) => ({
  value,
  label: COLLECTION_FILTER_LABELS[value],
}))

const brl = (amount: string): string => formatMoney(parseAmount(amount))

const plural = (count: number, one: string, many: string): string =>
  `${String(count)} ${count === 1 ? one : many}`

const dayLabel = (day: string): string => {
  const [, month, date] = day.split('-')
  return `${date ?? ''}/${month ?? ''}`
}

const ago = (iso: string, now: Date): string => {
  const days = Math.floor(
    (now.getTime() - new Date(iso).getTime()) / 86_400_000
  )
  if (days <= 0) return 'hoje'
  if (days === 1) return 'ontem'
  return `há ${String(days)} dias`
}

const QueueCard = ({
  item,
  active,
  now,
  onSelect,
}: {
  item: CollectionQueueItem
  active: boolean
  now: Date
  onSelect: () => void
}): ReactNode => (
  <button
    type="button"
    aria-current={active ? 'true' : undefined}
    data-party={item.partyId}
    onClick={onSelect}
    className={cn(
      'flex w-full items-start gap-3 rounded-(--radius-md) border px-3 py-2.5 text-left transition-colors',
      'focus-visible:outline-2 focus-visible:outline-(--border-focus)',
      active
        ? 'border-(--brand-primary) bg-(--brand-primary-subtle)'
        : 'border-(--border-subtle) bg-(--surface) hover:border-(--border-strong) hover:bg-(--surface-hover)'
    )}
  >
    <Avatar label={item.name} seed={item.partyId} />
    <span className="flex min-w-0 flex-1 flex-col gap-0.5">
      <span className="flex items-baseline justify-between gap-2">
        <span className="truncate text-(length:--text-sm) font-(--weight-medium) text-(--text-primary)">
          {item.name}
        </span>
        <span
          className={cn(
            'shrink-0 font-(--font-mono) text-(length:--text-sm) tabular-nums',
            item.overdueCount > 0 ? 'text-(--danger)' : 'text-(--text-primary)'
          )}
        >
          {brl(item.overdueCount > 0 ? item.overdueAmount : item.outstanding)}
        </span>
      </span>
      <span className="text-(length:--text-xs) text-(--text-secondary)">
        {item.overdueCount > 0
          ? `${plural(item.oldestOverdueDays, 'dia', 'dias')} · ${plural(item.overdueCount, 'título vencido', 'títulos vencidos')}`
          : item.nextDueAt === null
            ? plural(item.openCount, 'título', 'títulos')
            : `vence ${new Date(item.nextDueAt).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })}`}
      </span>
      <span className="flex flex-wrap items-center gap-1">
        {item.promise === null ? null : item.promise.state === 'broken' ? (
          <Badge tone="danger">Promessa quebrada</Badge>
        ) : (
          <Badge
            tone={item.promise.state === 'due_today' ? 'warning' : 'success'}
          >
            {`Prometeu ${brl(item.promise.amount)} p/ ${item.promise.state === 'due_today' ? 'hoje' : dayLabel(item.promise.promisedFor)}`}
          </Badge>
        )}
        {item.creditBlocked ? (
          <Badge tone="neutral">Crédito bloqueado</Badge>
        ) : null}
      </span>
      <span className="text-(length:--text-xs) text-(--text-muted)">
        {item.lastContact === null
          ? 'nenhum contato ainda'
          : `último contato ${ago(item.lastContact.at, now)}${item.lastContact.actorName === null ? '' : ` · ${item.lastContact.actorName}`}`}
      </span>
    </span>
  </button>
)

export const CobrancasPage = (): ReactNode => {
  const { cliente } = useSearch({ from: '/shell/cobrancas' })

  const [filter, setFilter] = useState<CollectionFilter>(
    cliente === undefined ? 'overdue' : 'all'
  )
  const [search, setSearch] = useState('')
  const debouncedSearch = useDebounced(search)
  const [sort, setSort] = useState<'oldest' | 'amount' | 'name'>('oldest')
  const [page, setPage] = useState(1)
  const [selectedId, setSelectedId] = useState<string | null>(cliente ?? null)

  const queue = useQuery({
    queryKey: [
      'collections',
      'queue',
      { filter, search: debouncedSearch, sort, page },
    ],
    queryFn: ({ signal }) =>
      fetchCollectionQueue(
        {
          filter,
          search: debouncedSearch,
          sort,
          page,
          pageSize: PAGE_SIZE,
        },
        signal
      ),
    placeholderData: keepPreviousData,
    refetchInterval: 60_000,
  })

  const items = queue.data?.items ?? []
  const summary = queue.data?.summary
  const counts = queue.data?.counts
  const now = new Date()

  const move = (step: number): void => {
    if (items.length === 0) return
    const index = items.findIndex((item) => item.partyId === selectedId)
    const next =
      index === -1 ? 0 : Math.min(items.length - 1, Math.max(0, index + step))
    const target = items[next]
    if (target === undefined) return
    setSelectedId(target.partyId)
    document
      .querySelector<HTMLButtonElement>(`[data-party="${target.partyId}"]`)
      ?.focus()
  }

  const onQueueKey = (event: KeyboardEvent<HTMLDivElement>): void => {
    if (event.key === 'ArrowDown' || event.key === 'j') {
      event.preventDefault()
      move(1)
    } else if (event.key === 'ArrowUp' || event.key === 'k') {
      event.preventDefault()
      move(-1)
    }
  }

  return (
    <Page>
      <PageHeader
        title="Cobranças"
        description="Quem deve, há quanto tempo e o que foi combinado. Clique num cliente para cobrar, receber ou renegociar."
      />

      <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-5">
        <StatTile
          label="Vencido"
          loading={queue.isPending}
          error={queue.isError}
          value={
            <span className="text-(--danger)">
              {brl(summary?.overdueAmount ?? '0.00')}
            </span>
          }
        />
        <StatTile
          label="Clientes em atraso"
          loading={queue.isPending}
          error={queue.isError}
          value={String(summary?.overdueCustomers ?? 0)}
        />
        <StatTile
          label="Promessas para hoje"
          loading={queue.isPending}
          error={queue.isError}
          value={String(summary?.promisesToday ?? 0)}
        />
        <StatTile
          label="Promessas quebradas"
          loading={queue.isPending}
          error={queue.isError}
          value={
            <span
              className={cn(
                (summary?.brokenPromises ?? 0) > 0
                  ? 'text-(--danger)'
                  : undefined
              )}
            >
              {String(summary?.brokenPromises ?? 0)}
            </span>
          }
        />
        <StatTile
          label="Vence em 7 dias"
          loading={queue.isPending}
          error={queue.isError}
          value={brl(summary?.dueSoonAmount ?? '0.00')}
        />
      </div>

      <div className="flex flex-col gap-4 lg:flex-row lg:items-start">
        <section
          aria-label="Fila de cobrança"
          className={cn(
            'flex-col gap-3 lg:flex lg:w-96 lg:shrink-0',
            selectedId === null ? 'flex' : 'hidden'
          )}
        >
          <Tabs
            value={filter}
            onValueChange={(value) => {
              setFilter(value as CollectionFilter)
              setPage(1)
            }}
          >
            <TabsList className="flex-wrap">
              {TABS.map((tab) => (
                <TabsTrigger key={tab.value} value={tab.value}>
                  {tab.label}
                  {counts === undefined ? null : (
                    <span className="ml-1.5 rounded-full bg-(--surface-sunken) px-1.5 text-(length:--text-xs) tabular-nums">
                      {String(counts[tab.value])}
                    </span>
                  )}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>

          <div className="flex gap-2">
            <SearchInput
              value={search}
              onChange={(event) => {
                setSearch(event.target.value)
                setPage(1)
              }}
              placeholder="Nome ou CPF/CNPJ"
              aria-label="Buscar cliente"
              className="flex-1"
            />
            <Select
              aria-label="Ordem"
              value={sort}
              onChange={(event) => {
                setSort(event.target.value as 'oldest' | 'amount' | 'name')
                setPage(1)
              }}
              className="w-36"
            >
              <option value="oldest">Mais atrasado</option>
              <option value="amount">Maior dívida</option>
              <option value="name">Nome</option>
            </Select>
          </div>

          {queue.isPending ? (
            <div className="flex justify-center py-8">
              <Spinner label="Carregando a fila" />
            </div>
          ) : queue.isError ? (
            <LoadFailed
              what="a fila de cobrança"
              onRetry={() => {
                void queue.refetch()
              }}
            />
          ) : items.length === 0 ? (
            <EmptyState
              icon={<HandCoins aria-hidden="true" className="h-8 w-8" />}
              title={
                debouncedSearch.trim().length > 0
                  ? 'Nenhum cliente encontrado'
                  : filter === 'overdue'
                    ? 'Ninguém em atraso'
                    : filter === 'broken'
                      ? 'Nenhuma promessa quebrada'
                      : filter === 'promised_today'
                        ? 'Nenhuma promessa para hoje'
                        : 'Nada nesta aba'
              }
              {...(filter === 'overdue' && debouncedSearch.trim().length === 0
                ? {
                    description:
                      'Quando um título a receber vencer, o cliente aparece aqui.',
                  }
                : {})}
            />
          ) : (
            <div
              role="list"
              aria-label="Clientes"
              onKeyDown={onQueueKey}
              className="flex flex-col gap-2"
            >
              {items.map((item) => (
                <div role="listitem" key={item.partyId}>
                  <QueueCard
                    item={item}
                    active={item.partyId === selectedId}
                    now={now}
                    onSelect={() => {
                      setSelectedId(item.partyId)
                    }}
                  />
                </div>
              ))}
            </div>
          )}

          {(queue.data?.total ?? 0) > PAGE_SIZE ? (
            <Pagination
              page={page}
              pageSize={PAGE_SIZE}
              total={queue.data?.total ?? 0}
              onPageChange={setPage}
              label="Páginas da fila"
            />
          ) : null}
        </section>

        <section
          aria-label="Cliente"
          className={cn(
            'min-w-0 flex-1 rounded-(--radius-lg) border border-(--border-subtle) bg-(--surface) p-4',
            selectedId === null ? 'hidden lg:block' : 'block'
          )}
        >
          {selectedId === null ? (
            <EmptyState
              icon={<HandCoins aria-hidden="true" className="h-8 w-8" />}
              title="Escolha um cliente na fila"
              description="Os títulos, a promessa e tudo o que já foi feito aparecem aqui."
            />
          ) : (
            <CollectionPanel
              key={selectedId}
              partyId={selectedId}
              onBack={() => {
                setSelectedId(null)
              }}
            />
          )}
        </section>
      </div>
    </Page>
  )
}
